/**
 * Everything out on the counter, before step one (#1338).
 *
 * ⛔ SPLIT BY WHOSE PLATE, not into one flat list. That split is the only thing
 * a normal ingredient list cannot tell you, and it is the thing this cook needs
 * to know before they start: "Ground beef" in a single column looks like an
 * ingredient of the dish, where "only for Sam's plate" is an instruction.
 *
 * ⚠️ A fork nobody in this household wants is not here at all — the server
 * decides that, using the same bar the fork STEPS use, so the counter and the
 * recipe cannot disagree.
 */
import type { Gather } from "../api/twoPlates";

export function GatherCard({ gather }: { gather: Gather }) {
  const nothing = gather.base.length === 0 && gather.forks.length === 0;

  return (
    <div className="flex-1">
      <p className="mt-4 text-2xl leading-snug">Get this out.</p>
      <p className="text-muted mt-2 text-sm">
        Everything for tonight, before you start. Amounts are for your table.
      </p>

      {nothing && (
        <p className="text-muted mt-6 text-sm">
          Nothing to gather — this one is all technique.
        </p>
      )}

      {gather.base.length > 0 && (
        <div className="border-line mt-6 rounded-xl border p-4">
          <p className="text-muted text-xs uppercase tracking-wide">Everyone</p>
          <ul className="mt-2 flex flex-col gap-1.5 text-base">
            {gather.base.map((l) => (
              // ⚠️ Inline and wrapping, NOT a justify-between row. On a 360px
              // phone the card is about 288px wide, and a long name beside a
              // shrink-0 badge pushed the whole screen sideways.
              <li key={l.item} className="break-words">
                {/* ⚠️ Amount first and emphasised. At a counter you are
                    checking quantities against what is in front of you, so the
                    number is what the eye should land on. */}
                {l.amount && <span className="font-semibold">{l.amount} </span>}
                {l.item}
                {/* ⚠️ Only said when a restriction overrode the household's usual
                    choice. Otherwise it is noise on every line — and this is the
                    one case where somebody would otherwise reach for a packet
                    they did not buy. */}
                {l.forcedByRestriction && (
                  <span className="text-muted ml-2 text-[11px]">
                    {l.style === "scratch" ? "not the packet" : "the packet"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {gather.forks.map((f) => (
        <div
          key={f.group}
          className="mt-3 rounded-xl border p-4"
          style={{ borderColor: "var(--color-plate-b)" }}
        >
          <p className="text-plate-b break-words text-xs uppercase tracking-wide">
            {/* Whose plate, by name. "Fork: beef" means nothing at a stove.
                Wraps, because a household of five names is a long line. */}
            Only for {f.forPlates.join(" and ")}
          </p>
          <ul className="mt-2 flex flex-col gap-1.5 text-base">
            {f.lines.map((l) => (
              <li key={l.item} className="break-words">
                {/* Scaled to the plates that want it, not to the household —
                    one beef-eater in a house of five gets beef for one. */}
                {l.amount && <span className="font-semibold">{l.amount} </span>}
                {l.item}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {gather.forks.length > 0 && (
        <p className="text-muted mt-4 text-xs">
          Keep those to one side. The recipe says when they go in.
        </p>
      )}
    </div>
  );
}
